
import { AfterViewInit, Component, inject, input, output, signal } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { AnnotationEditor } from '@markerjs/markerjs-ui';
import { AnnotationState, MarkerBaseState, MarkerView } from '@markerjs/markerjs3';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { AreaMap } from 'src/app/models/area-map.model';
import { Site } from 'src/app/models/site.model';
import { AreasService } from 'src/app/services/areas.service';
import { SiteAreasService } from 'src/app/services/site-areas.service';
import { RentersDialogComponent } from '../renters/renters.dialog';

@Component({
  selector: 'app-area-map',
  template: `
   <div [id]="'div-' + id()">
   </div>
    `,
  imports: [
    AppCommonModule,
  ],
})
export class AppAreaMapComponent implements AfterViewInit {
  private areasService = inject(AreasService);
  private modalCtrl = inject(ModalController);
  private siteAreasService = inject(SiteAreasService);

  private maps = signal(Array.from<AreaMap>([]));

  private initialHistory: AnnotationState;

  width = input(1200);
  height = input(1000);
  imgElement = input.required<HTMLImageElement>();
  site = input<Site>();
  floor = input<string>();
  point = input<AreaMap>(null);
  selectArea = input(false);

  cancelled = output();
  selectedArea = output<string>();

  id = signal(new Date().getTime().toString());

  private async addMarker(state: AnnotationState) {
    const allAreas = await firstValueFrom(this.areasService.getSiteAreas(this.site().guid));
    const areas = allAreas.filter((it) => !this.maps().map((itt) => itt.area).includes(it.guid) && it.floor === this.floor());
    return this.modalCtrl.create({ component: RentersDialogComponent, componentProps: { areas }, cssClass: ['stack-modal'] }).then(async (m) => {
      m.present();
      return m.onDidDismiss().then((data) => {
        if (data.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          state.markers.map((mark) => {
            mark.notes = data.data.area.guid;
          });
          const item: AreaMap = {
            site: this.site().guid,
            area: data.data.area.guid,
            areaName: data.data.area.name,
            tenant: data.data.item ? { guid: data.data.item.guid, name: data.data.item.company } : null,
            state,
            floor: this.floor(),
          };
          this.siteAreasService.saveMap(item).then((doc) => {
            item.guid = doc.id;
            this.maps.update((val) => [...val, item]);
            if (this.initialHistory == null) {
              this.initialHistory = state;
            } else {
              this.initialHistory.markers.push(...state.markers);
            }
          });
          return data.data.area.guid as string;
        }
      });
    });
  }

  private deleteMarkers(markers: MarkerBaseState[]) {
    markers.map((it) => {
      const map = this.maps().find((itt) => it.notes === itt.area);
      this.siteAreasService.deleteMap(map);
      this.initialHistory.markers = this.initialHistory.markers.filter((itt) => itt.notes !== it.notes);
      this.maps.update((val) => val.filter((itt) => itt.guid !== map.guid));
    });
  }

  private updateMarkers(markers: MarkerBaseState[]) {
    markers.map((it) => {
      const map = this.maps().find((itt) => it.notes === itt.area);
      const updatedMarkers = markers.filter((itt) => itt.notes === it.notes);
      // eslint-disable-next-line @typescript-eslint/naming-convention
      this.siteAreasService.updateOnlyMap(map.guid, { 'state.markers': updatedMarkers });
    });
  }

  private setupMarker() {
    const div = document.querySelector<HTMLDivElement>('#div-' + this.id());

    if (this.point() != null) {
      const markerView = new MarkerView();
      markerView.targetImage = this.imgElement();
      const el = this.imgElement();
      const widthAspectRatio = this.width() / el.width;
      const heightAspectRatio = this.height() / el.height;
      const finalAspectRatio = Math.min(widthAspectRatio, heightAspectRatio);
      const height = el.height * finalAspectRatio;
      const width = el.width * finalAspectRatio;
      markerView.targetHeight = height;
      markerView.targetWidth = width;
      markerView.show(this.point().state);
      div.appendChild(markerView);
      markerView.addEventListener('viewrestorestate', () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const canvasContainer = (markerView as any)._canvasContainer as HTMLDivElement;
        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const marker = this.point().state?.markers[0] as any;
          if (marker.points != null) {
            canvasContainer.scrollTo(marker.points[0].x - 10, marker.points[0].y);
          } else if (marker.left != null) {
            canvasContainer.scrollTo(marker.left - 10, marker.top);
          }
        }, 50);
      });
    } else if (this.selectArea()) {
      const markerView = new MarkerView();
      markerView.targetImage = this.imgElement();
      const el = this.imgElement();
      const widthAspectRatio = this.width() / el.width;
      const heightAspectRatio = this.height() / el.height;
      const finalAspectRatio = Math.min(widthAspectRatio, heightAspectRatio);
      const height = el.height * finalAspectRatio;
      const width = el.width * finalAspectRatio;
      markerView.targetHeight = height;
      markerView.targetWidth = width;
      div.appendChild(markerView);

      this.siteAreasService.getListMap({ site: this.site().guid, floor: this.floor() }).pipe(
        take(1),
      ).subscribe((maps) => {
        this.maps.set(maps);
        if (maps.length > 0) {
          const history = maps[0].state;
          if (maps.length > 1) {
            maps.map((map, index) => {
              if (index > 0) {
                history.markers.push(...map.state.markers);
              }
            });
          }
          markerView.show(history);

          markerView.addEventListener('markerclick', (ev) => {
            const area = ev.detail.marker.notes;
            this.selectedArea.emit(area);
          });
        }
      });
    } else {
      const editor = new AnnotationEditor();
      editor.targetImage = this.imgElement();
      div.appendChild(editor);

      /*editor.markerArea.addEventListener('areastatechange', (ev) => {
        const state = ev.detail.markerArea.getState();
      });*/

      editor.addEventListener('editorsave', async (event) => {
        const state = event.detail.state;
        if (state.markers.length < this.initialHistory?.markers.length) {
          // Deleted
          let deletedMarkers = this.initialHistory.markers.filter((it) => state.markers.some((itt) => itt.notes !== it.notes));
          if (state.markers.length === 0) {
            deletedMarkers = this.initialHistory.markers;
          }
          this.deleteMarkers(deletedMarkers);
        }
        state.markers.map((it) => {
          if (it.notes?.length > 0) {
            const updated = state.markers.filter((itt) => it.notes === itt.notes);
            if (updated?.length > 0) {
              // Update
              this.updateMarkers(updated);
            }
          }
        });
        if (state.markers.length > (this.initialHistory?.markers.length ?? 0)) {
          // Added
          const newMarkers = state.markers.filter((it) => it.notes == null || it.notes === '');
          const newState = Object.assign({}, { ...state }, { markers: newMarkers });
          const areaGuid = await this.addMarker(newState);
          newMarkers.map((it) => {
            it.notes = areaGuid;
          });
        }
        editor.restoreState(state);
      });
      this.siteAreasService.getListMap({ site: this.site().guid, floor: this.floor() }).pipe(
        take(1),
      ).subscribe((maps) => {
        this.maps.set(maps);
        if (maps.length > 0) {
          const history = maps[0].state;
          if (maps.length > 1) {
            maps.map((map, index) => {
              if (index > 0) {
                history.markers.push(...map.state.markers);
              }
            });
          }
          this.initialHistory = history;
          editor.restoreState(history);
        }
      });
    }
  }

  ngAfterViewInit() {
    this.setupMarker();
  }
}
